import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chargerStatusRefetchInterval, refetchApiInterval } from "../../shared/constants";
import { getActiveTransactionId, getChargerConnectorData, getChargerConnectorsEnergyHistogram, getChargerDetails, getChargerHistory, getChargerTransactionList, getChargerTransactions, getChargerTransactionsCount, getProjectChargerFaultsCount, getProjectChargerFaultsList } from "../api/eflow-charger-dashboard-service";
/**
 * React Query hook to fetch charger details by charger ID
 * @param chargerId - The unique identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @param enableRefetchInterval - Boolean to enable/disable auto-refetch (default: true)
 * @param timezoneOffset - Optional timezone offset (e.g., "UTC+01:00", "+01:00")
 * @returns React Query result with charger data
 */
export const useChargerDetailsQuery = (chargerId, projectId, enabled = true, refetchApiData, enableRefetchInterval = true, timezoneOffset) => {
    return useQuery({
        enabled: enabled && !!chargerId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        queryFn: async () => {
            return await getChargerDetails(chargerId, projectId, timezoneOffset);
        },
        queryKey: ["chargerDetails", chargerId, projectId, refetchApiData, timezoneOffset],
        refetchInterval: enableRefetchInterval ? chargerStatusRefetchInterval : false,
        refetchOnWindowFocus: false, // Prevent refetch on window focus
        retry: 1,
        staleTime: chargerStatusRefetchInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch transactions for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., startDate, endDate, limit, etc.)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with charger transactions data
 */
export const useChargerTransactionsQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerTransactions(assetId, projectId, queryParams);
        },
        queryKey: ["chargerTransactions", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * Imperative lookup for the Stop-charger flow's active transaction. All of the 1/2/5-day window
 * widening, cursor traversal, and connector filtering lives in the service
 * (`getActiveTransactionId`); this hook only wires that service call into React Query.
 *
 * `connectorId` is only known at click time, so this returns a fetch function instead of a
 * `useQuery` result — `queryClient.fetchQuery` takes it as a plain argument and includes it in the
 * query key, rather than a `useQuery`-based hook where the key can only reflect render-time values.
 *
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param timezoneOffset - Project timezone offset used to build the lookback windows
 * @returns `fetchActiveTransactionId(connectorId)` — call it to resolve the active transactionId
 */
export const useActiveTransactionIdQuery = (assetId, projectId, timezoneOffset) => {
    const queryClient = useQueryClient();
    const fetchActiveTransactionId = (connectorId) => queryClient.fetchQuery({
        queryFn: () => getActiveTransactionId(assetId, projectId, connectorId, timezoneOffset),
        queryKey: ["activeTransactionId", assetId, projectId, connectorId, timezoneOffset],
        retry: false,
        staleTime: 0
    });
    return { fetchActiveTransactionId };
};
/**
 * React Query hook to fetch transaction count for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., startDate, endDate, etc.)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with transaction count
 */
export const useChargerTransactionsCountQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerTransactionsCount(assetId, projectId, queryParams);
        },
        queryKey: ["chargerTransactionsCount", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch charger history for a specific charger
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., fromTime, toTime)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with charger history data
 */
export const useChargerHistoryQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerHistory(assetId, projectId, queryParams);
        },
        queryKey: ["chargerHistory", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
/**
 * React Query hook to fetch energy histogram data for charger connectors
 * @param assetId - The unique asset identifier for the charger
 * @param projectId - The unique identifier for the project
 * @param queryParams - Optional query parameters (e.g., fromTime, toTime)
 * @param enabled - Boolean to enable/disable the query (default: true)
 * @param refetchApiData - Optional counter to force refetch
 * @returns React Query result with energy histogram data
 */
export const useChargerConnectorsEnergyHistogramQuery = (assetId, projectId, queryParams, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!assetId && !!projectId,
        gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        placeholderData: undefined, // Don't show previous data while fetching new data
        queryFn: async () => {
            return await getChargerConnectorsEnergyHistogram(assetId, projectId, queryParams);
        },
        queryKey: ["chargerConnectorsEnergyHistogram", assetId, projectId, queryParams, refetchApiData],
        refetchInterval: refetchApiInterval, // Auto-refresh every 5 minutes
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval // Align with refetchInterval for consistent caching
    });
};
export const useChargerConnectorDataQuery = (projectId, assetId, partyNumber, enabled = true, refetchApiData) => {
    return useQuery({
        enabled: enabled && !!projectId && !!assetId && !!partyNumber,
        gcTime: 5 * 60 * 1000,
        placeholderData: undefined,
        queryFn: async () => {
            return await getChargerConnectorData(projectId, assetId, partyNumber);
        },
        queryKey: ["chargerConnectorData", projectId, assetId, partyNumber, refetchApiData],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: refetchApiInterval
    });
};
export const useChargerTransactionListQuery = (chargerTransactionSearchRequest, queryParams) => {
    return useQuery({
        enabled: false,
        gcTime: 0,
        queryFn: async () => {
            return await getChargerTransactionList(chargerTransactionSearchRequest, queryParams);
        },
        queryKey: ["chargerTransactionList", queryParams],
        retry: 0
    });
};
export const useProjectChargerFaultsListQuery = (params, chargerFaultSearchRequest, options) => {
    return useQuery({
        enabled: (options?.enabled ?? true) && !!params.assetId && !!params.projectId,
        gcTime: 5 * 60 * 1000,
        placeholderData: undefined,
        queryFn: async () => {
            return await getProjectChargerFaultsList(params, chargerFaultSearchRequest);
        },
        queryKey: ["projectChargerFaultsList", params, chargerFaultSearchRequest, options?.refetchKey],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 0,
        staleTime: refetchApiInterval
    });
};
export const useProjectChargerFaultsCountQuery = (params, chargerFaultCountRequest, options) => {
    return useQuery({
        enabled: (options?.enabled ?? true) && !!params.assetId && !!params.projectId,
        gcTime: 5 * 60 * 1000,
        queryFn: async () => {
            return await getProjectChargerFaultsCount(params, chargerFaultCountRequest);
        },
        queryKey: ["projectChargerFaultsCount", params, chargerFaultCountRequest, options?.refetchKey],
        refetchInterval: refetchApiInterval,
        refetchOnWindowFocus: false,
        retry: 0,
        staleTime: refetchApiInterval
    });
};
